// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";
import { env } from "~/env.mjs";

const isE2EUser = (event: Sentry.Event): boolean => {
  const email = event.user?.email?.toLowerCase();
  const username = event.user?.username?.toLowerCase();
  return Boolean(
    email?.includes("e2e+") ||
      email?.includes("@example.com") ||
      username?.includes("e2e"),
  );
};

const isFetchFailure = (event: Sentry.Event): boolean => {
  const exceptionValues = event.exception?.values ?? [];
  return exceptionValues.some((value) =>
    value.value?.toLowerCase().includes("failed to fetch"),
  );
};

const isLocalhostRequest = (event: Sentry.Event): boolean => {
  const url = event.request?.url?.toLowerCase();
  return Boolean(url?.includes("localhost"));
};

// Avoid double-init if Sentry is initialized elsewhere (e.g. Next/Sentry integration)
if (!Sentry.getClient()) {
  Sentry.init({
    dsn: env.NEXT_PUBLIC_SENTRY_DSN,
    enabled: env.NEXT_PUBLIC_NODE_ENV === "production",

    // Adjust this value in production, or use tracesSampler for greater control
    tracesSampleRate: 1,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug: false,

    replaysOnErrorSampleRate: 1.0,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // You can remove this option if you're not planning to use the Sentry Session Replay feature:
    integrations: [
      Sentry.replayIntegration({
        // Additional Replay configuration goes in here, for example:
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    beforeSend(event) {
      const e2eAuthEnabled =
        process.env.NEXT_PUBLIC_ENABLE_TEST_CREDENTIALS_AUTH === "true";

      // Ignore expected E2E browser-network failures for test-credentials users.
      if (
        isFetchFailure(event) &&
        isE2EUser(event) &&
        (e2eAuthEnabled || isLocalhostRequest(event))
      ) {
        return null;
      }

      return event;
    },

    sendDefaultPii: true,
  });
}
